import { useState, useEffect } from "react";
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  Divider,
  Card,
  CardContent,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import QrCodeIcon from "@mui/icons-material/QrCode";
import NfcIcon from "@mui/icons-material/Nfc";
import { useSelector } from "react-redux";
import { Navigate } from "react-router-dom";
import {
  collection,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
  getDoc,
} from "firebase/firestore";
import { db } from "../../services/firebase/config";
import {
  generateNfcToken,
  getUserNfcTokens,
  revokeNfcToken,
} from "../../services/firebase/nfcAuth";
import {
  fetchUserTokens,
  fetchUserData,
  generateTokenAndUrl,
  revokeToken,
  determineBaseUrl,
} from "../../services/nfc/nfcCardService";

function NfcCardManagement() {
  const user = useSelector((state) => state.auth.user);
  const isAdmin = useSelector((state) => state.auth.isAdmin);
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState("");
  const [selectedUserData, setSelectedUserData] = useState(null);
  const [userTokens, setUserTokens] = useState([]);
  const [generatedUrl, setGeneratedUrl] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [copySuccess, setCopySuccess] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    tokenId: null,
  });
  const [baseUrl, setBaseUrl] = useState("https://myneighbor.yiswork.com");

  // Fetch users on component mount
  useEffect(() => {
    const fetchUsers = async () => {
      setLoading(true);
      try {
        const usersSnapshot = await getDocs(collection(db, "users"));
        const usersData = usersSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        // Sort users alphabetically by email
        const sortedUsers = usersData.sort((a, b) => {
          const emailA = (a.email || "").toLowerCase();
          const emailB = (b.email || "").toLowerCase();
          return emailA.localeCompare(emailB);
        });

        setUsers(sortedUsers);
      } catch (error) {
        console.error("Error fetching users:", error);
        setError("Failed to load users");
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
    setBaseUrl(determineBaseUrl());
  }, []);

  // Fetch user tokens when a user is selected
  useEffect(() => {
    if (selectedUser) {
      loadUserData();
      loadUserTokens();
    } else {
      setSelectedUserData(null);
      setUserTokens([]);
    }
  }, [selectedUser]);

  const loadUserData = async () => {
    try {
      const userData = await fetchUserData(selectedUser);
      setSelectedUserData(userData);
    } catch (error) {
      setError(error.message);
    }
  };

  const loadUserTokens = async () => {
    if (!selectedUser) return;

    setLoading(true);
    try {
      const tokens = await fetchUserTokens(selectedUser);
      setUserTokens(tokens);
    } catch (error) {
      setError("Failed to load user tokens");
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateToken = async () => {
    if (!selectedUser) {
      setError("Please select a user first");
      return;
    }

    setLoading(true);
    setError("");
    setSuccess("");
    setGeneratedUrl("");

    try {
      const { nfcUrl } = await generateTokenAndUrl(
        selectedUser,
        selectedUserData,
        baseUrl
      );
      setGeneratedUrl(nfcUrl);
      setSuccess("Token generated successfully");

      // Refresh user data and tokens
      await loadUserData();
      await loadUserTokens();
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRevokeToken = async () => {
    setLoading(true);
    try {
      await revokeToken(confirmDialog.tokenId);
      setSuccess("Token revoked successfully");
      await loadUserTokens();
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
      setConfirmDialog({ open: false, tokenId: null });
    }
  };

  const openConfirmDialog = (tokenId) => {
    setConfirmDialog({ open: true, tokenId });
  };

  const closeConfirmDialog = () => {
    setConfirmDialog({ open: false, tokenId: null });
  };

  const handleCopyUrl = () => {
    // Ensure this function is defined
    if (generatedUrl) {
      navigator.clipboard
        .writeText(generatedUrl)
        .then(() => {
          setCopySuccess(true);
        })
        .catch((err) => {
          console.error("Failed to copy URL: ", err);
          setError("Failed to copy URL. Please copy it manually.");
        });
    }
  };

  const handleSnackbarClose = () => {
    setCopySuccess(false);
  };

  if (!user || !isAdmin) {
    return <Navigate to="/" replace />;
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 8 }}>
      <Paper
        elevation={3}
        sx={{
          p: 3,
          backgroundColor: "#f2f0e4",
          border: "1px solid #000000",
          borderRadius: 0,
        }}
      >
        <Typography variant="h4" gutterBottom sx={{ fontWeight: "bold" }}>
          NFC CARD MANAGEMENT
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            Generate NFC Login Token
          </Typography>

          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={8}>
              <FormControl fullWidth>
                <InputLabel>Select User</InputLabel>
                <Select
                  value={selectedUser}
                  label="Select User"
                  onChange={(e) => setSelectedUser(e.target.value)}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {users.map((user) => (
                    <MenuItem key={user.id} value={user.id}>
                      {user.email}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={4}>
              <Button
                variant="contained"
                fullWidth
                onClick={handleGenerateToken}
                disabled={!selectedUser || loading}
                startIcon={<NfcIcon />}
                sx={{
                  backgroundColor: "#000000",
                  color: "#ffffff",
                  borderRadius: 0,
                  height: "56px",
                  "&:hover": {
                    backgroundColor: "#333333",
                  },
                }}
              >
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  "Generate Token"
                )}
              </Button>
            </Grid>
          </Grid>

          {generatedUrl && (
            <Card
              variant="outlined"
              sx={{
                mt: 3,
                border: "1px solid #000000",
                borderRadius: 0,
              }}
            >
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  NFC Card URL
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Program this URL into the NFC card using NFC Tools app:
                </Typography>

                <Box
                  sx={{
                    p: 2,
                    backgroundColor: "#f8f8f8",
                    borderRadius: 1,
                    wordBreak: "break-all",
                    position: "relative",
                    border: "1px dashed #000000",
                    mt: 1,
                  }}
                >
                  <Typography variant="body2" fontFamily="monospace">
                    {generatedUrl}
                  </Typography>
                  <Tooltip title="Copy URL">
                    <IconButton
                      size="small"
                      onClick={handleCopyUrl} // Assign the handler here
                      sx={{
                        position: "absolute",
                        top: 8,
                        right: 8,
                        backgroundColor: "#ffffff",
                        "&:hover": {
                          backgroundColor: "#f0f0f0",
                        },
                      }}
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>

                <Typography variant="body2" sx={{ mt: 2 }}>
                  Instructions:
                </Typography>
                <ol>
                  <li>Open the NFC Tools app on your iPhone</li>
                  <li>Tap "Write" and select "Add a record"</li>
                  <li>Choose "URL / URI"</li>
                  <li>Paste the URL above</li>
                  <li>Tap "OK" and then "Write"</li>
                  <li>Hold the NFC card to the back of your iPhone</li>
                </ol>
              </CardContent>
            </Card>
          )}
        </Box>

        <Divider sx={{ my: 4 }} />

        <Typography variant="h6" gutterBottom>
          User's NFC Tokens
        </Typography>

        {selectedUser ? (
          loading && userTokens.length === 0 ? (
            <Box display="flex" justifyContent="center" my={4}>
              <CircularProgress />
            </Box>
          ) : userTokens.length === 0 ? (
            <Typography variant="body1" align="center" my={4}>
              No tokens found for this user
            </Typography>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Token ID</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell>Expires</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {userTokens.map((token) => (
                    <TableRow key={token.id}>
                      <TableCell>
                        <Tooltip title={token.id}>
                          <Typography>{token.id.substring(0, 8)}...</Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        {token.createdAt
                          ? token.createdAt.toLocaleDateString()
                          : "N/A"}
                      </TableCell>
                      <TableCell>
                        {token.expiresAt
                          ? token.expiresAt.toLocaleDateString()
                          : "Never"}
                      </TableCell>
                      <TableCell>
                        <IconButton
                          color="error"
                          onClick={() => openConfirmDialog(token.id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )
        ) : (
          <Typography variant="body1" align="center" my={4}>
            Select a user to view their tokens
          </Typography>
        )}
      </Paper>

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialog.open} onClose={closeConfirmDialog}>
        <DialogTitle>Confirm Revocation</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to revoke this NFC token? The associated NFC
            card will no longer work for authentication.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeConfirmDialog}>Cancel</Button>
          <Button onClick={handleRevokeToken} color="error">
            Revoke Token
          </Button>
        </DialogActions>
      </Dialog>

      {/* Copy Success Snackbar */}
      <Snackbar
        open={copySuccess}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        message="URL copied to clipboard"
      />
    </Container>
  );
}

export default NfcCardManagement;
